import React, { useMemo } from 'react';
import { Skeleton, Tabs } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { SalesLeadSourceDistribution, SourceMonthlyTrend } from '../../../types/dashboard';
import { MONTH_NAMES } from '../utils';

export interface LeadInterestReason {
  reason_id: string;
  reason_name: string;
  interest_status: string;
  count: number;
  percentage?: number;
}

interface SalesDetailSourcesChartProps {
  sources: SalesLeadSourceDistribution[] | null;
  sourceMonthlyTrends: SourceMonthlyTrend[] | null;
  leadsByInterestReason: LeadInterestReason[] | null;
  loading: boolean;
  glassCardStyle: React.CSSProperties;
}

const BRAND_COLORS = ['#7BAA18', '#1890ff', '#faad14', '#ff4d4f', '#13c2c2', '#722ed1', '#eb2f96'];

const SalesDetailSourcesChart: React.FC<SalesDetailSourcesChartProps> = ({
  sources,
  sourceMonthlyTrends,
  leadsByInterestReason,
  loading,
  glassCardStyle
}) => {
  // Pie/Donut Chart Options: Media Sebaran
  const sourcesPieOptions = useMemo(() => {
    if (!sources || sources.length === 0) return {};

    const data = sources.map(item => ({
      name: item.source_name,
      y: item.count,
    }));

    return {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: 250,
        style: { fontFamily: 'inherit' }
      },
      title: {
        text: 'Kontribusi Media Promosi',
        align: 'left',
        style: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }
      },
      tooltip: {
        pointFormat: '<b>{point.y} Leads ({point.percentage:.1f}%)</b>',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '{point.name}: {point.percentage:.0f}%',
            style: { color: '#475569', fontSize: '10px', textOutline: 'none' }
          },
          innerSize: '50%',
        }
      },
      colors: BRAND_COLORS,
      credits: { enabled: false },
      series: [{
        name: 'Media',
        colorByPoint: true,
        data
      }]
    };
  }, [sources]);

  // Monthly Spline Trends Chart Options: Tren Media
  const sourcesTrendOptions = useMemo(() => {
    if (!sourceMonthlyTrends || sourceMonthlyTrends.length === 0) return {};

    const sourcesSet = new Set<string>();
    const sourceNamesMap: Record<string, string> = {};
    
    sourceMonthlyTrends.forEach(item => {
      sourcesSet.add(item.source_id);
      sourceNamesMap[item.source_id] = item.source_name;
    });

    const uniqueSourceIds = Array.from(sourcesSet);
    const categories = MONTH_NAMES;
    
    const series = uniqueSourceIds.map(sourceId => {
      const data = Array.from({ length: 12 }, (_, monthIdx) => {
        const m = monthIdx + 1;
        const match = sourceMonthlyTrends.find(t => t.source_id === sourceId && t.month === m);
        return match ? match.count : 0;
      });

      return {
        name: sourceNamesMap[sourceId],
        data
      };
    });

    return {
      chart: {
        type: 'spline',
        height: 250,
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' }
      },
      title: {
        text: 'Tren Kontribusi Media Promosi (Bulanan)',
        align: 'left',
        style: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }
      },
      xAxis: {
        categories,
        crosshair: true,
        labels: { style: { color: '#64748b' } },
        lineColor: '#cbd5e1'
      },
      yAxis: {
        min: 0,
        title: { text: 'Jumlah Leads', style: { color: '#64748b' } },
        labels: { style: { color: '#64748b' } },
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash'
      },
      tooltip: {
        shared: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
        pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <span style="font-weight: bold; color: #0f172a;">{point.y} Leads</span><br/>'
      },
      plotOptions: {
        spline: {
          lineWidth: 2.5,
          marker: {
            enabled: true,
            radius: 3.5,
            symbol: 'circle'
          },
          dataLabels: { enabled: false },
          enableMouseTracking: true
        }
      },
      colors: BRAND_COLORS,
      credits: { enabled: false },
      series
    };
  }, [sourceMonthlyTrends]);

  // Horizontal Bar Chart for Reasons (Alasan Ketertarikan / Gagal / Closing)
  const reasonsChartOptions = useMemo(() => {
    if (!leadsByInterestReason || leadsByInterestReason.length === 0) return {};

    // Get unique reason names as categories
    const uniqueReasons = Array.from(new Set(leadsByInterestReason.map(item => item.reason_name)));
    
    // Get unique interest statuses as series
    const uniqueStatuses = Array.from(new Set(leadsByInterestReason.map(item => item.interest_status)));

    // For each interest status, build the data array matching the uniqueReasons categories order
    const series = uniqueStatuses.map(status => {
      const data = uniqueReasons.map(reasonName => {
        const match = leadsByInterestReason.find(
          item => item.interest_status === status && item.reason_name === reasonName
        );
        return match ? match.count : 0;
      });

      // Human-readable status mapping
      let displayName = status;
      let color: string | undefined = undefined;

      if (status === 'tidak_minat') {
        displayName = 'Gagal (Tidak Minat)';
        color = '#ff4d4f'; // Red color for "Tidak Minat"
      } else if (status === 'minat_proses') {
        displayName = 'Minat (Proses)';
      } else if (status === 'minat_pesan') {
        displayName = 'Minat (Pesan)';
      } else if (status === 'minat_tidak_pesan') {
        displayName = 'Minat (Tidak Pesan)';
      } else if (status === 'rsv') {
        displayName = 'Reservasi (RSV)';
      } else if (status === 'ok') {
        displayName = 'Closing (OK)';
      } else {
        // Capitalize words
        displayName = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }

      return {
        name: displayName,
        data,
        color
      };
    });

    return {
      chart: {
        type: 'bar',
        height: 250,
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' }
      },
      title: {
        text: 'Distribusi Alasan Ketertarikan & Penolakan Leads',
        align: 'left',
        style: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }
      },
      xAxis: {
        categories: uniqueReasons,
        title: { text: null },
        labels: { style: { color: '#64748b', fontSize: '10px' } },
        lineColor: '#cbd5e1'
      },
      yAxis: {
        min: 0,
        title: { text: 'Jumlah Leads', align: 'high', style: { color: '#64748b' } },
        labels: { overflow: 'justify', style: { color: '#64748b' } },
        gridLineColor: 'rgba(0, 0, 0, 0.04)',
        gridLineDashStyle: 'Dash'
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
        pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y} Leads</b>'
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: { fontSize: '10px', fontWeight: 'bold', textOutline: 'none' }
          }
        }
      },
      colors: BRAND_COLORS,
      credits: { enabled: false },
      series
    };
  }, [leadsByInterestReason]);

  return (
    <div className="glass-card-interactive" style={{ ...glassCardStyle, padding: '20px', minHeight: '340px' }}>
      {loading || !sources ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Tabs
          defaultActiveKey="share"
          type="line"
          items={[
            {
              key: 'share',
              label: 'Kontribusi Media',
              children: <HighchartsReact highcharts={Highcharts} options={sourcesPieOptions} />
            },
            {
              key: 'trend',
              label: 'Tren Bulanan Media',
              children: (
                sourceMonthlyTrends && sourceMonthlyTrends.length > 0 ? (
                  <HighchartsReact highcharts={Highcharts} options={sourcesTrendOptions} />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                    Tidak ada data tren bulanan media promosi untuk periode ini.
                  </div>
                )
              )
            },
            {
              key: 'reasons',
              label: 'Alasan Ketertarikan',
              children: (
                leadsByInterestReason && leadsByInterestReason.length > 0 ? (
                  <HighchartsReact highcharts={Highcharts} options={reasonsChartOptions} />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                    Tidak ada data analisis alasan ketertarikan untuk periode ini.
                  </div>
                )
              )
            }
          ]}
        />
      )}
    </div>
  );
};

export default SalesDetailSourcesChart;
